import {
  Typography,
  TextField,
  MenuItem,
  Divider,
  Box,
  Tooltip,
  useTheme,
  IconButton,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { DataGridPro, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { useEffect, useMemo, useState } from 'react';
import { textFieldProps, textFieldSelectProps } from '../../constants/form';
import usaStates from '../../../../static/usaStates.json';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormikProps, useFormik } from 'formik';
import { customerContactSchema } from './schemas/indexSchema';
import type { CustomerCreate, CustomerContactCreate } from '@repo/api-contract';
import { getFormikField } from '../../../../util/fornik-helpers';
import { useTranslation } from 'react-i18next';
import { usePersistedDataGridColumnPreferences } from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'customer-contacts' satisfies DataGridTableId;
const KEY_ADD_CUSTOMER = 'team.Modals.Customers.createAndEdit.Add Customer';
const KEY_GLOBAL_COMMON = 'team.Global.Common';
const KEY_GLOBAL_BUTTONS = 'team.Global.Buttons';
const columnPadding = '0 2px';

// We create a new type and add id in it because MUI DataGrid works only with ID
type ContactWithId = CustomerContactCreate & { id: string };

export default function ContactAndNotes({
  formik,
  viewMode,
}: {
  formik: FormikProps<CustomerCreate>;
  viewMode: boolean;
}): JSX.Element {
  const { t: tGlobal } = useTranslation([], {
    keyPrefix: 'team.Global.Buttons',
  });
  const { t: tGC } = useTranslation([], { keyPrefix: 'team.Global.Common' });
  const { t: tAddC } = useTranslation([], {
    keyPrefix: 'team.Modals.Customers.createAndEdit.Add Customer',
  });
  const theme = useTheme();

  // Basically MUI Grid requires Id to handle rows, but provided api structure does not have ids.
  // Because of that we need to create this state and use it for table.
  // In the functions below we update formik on every event. Basically we update 2 arrays at the same time (one has ids).
  const [contactsWithId, setContactsWithId] = useState<ContactWithId[]>([]);

  // edit Mode
  const [editMode, setEditMode] = useState<boolean>(false);
  const [contactBeforeEdit, setContactBeforeEdit] =
    useState<CustomerContactCreate>();
  const apiRef = useGridApiRef();

  const initialValues: CustomerContactCreate = {};

  const formikContact = useFormik({
    initialValues: initialValues,
    validationSchema: customerContactSchema,
    onSubmit: (values) => {
      alert(JSON.stringify(values, null, 2));
    },
  });

  const genUUID = () => {
    let uuid = uuidv4();
    return uuid;
  };

  // DELETE
  const handleDeleteContact = (id: any) => {
    const updatedContacts = contactsWithId?.filter(
      (contact) => contact.id !== id,
    );

    setContactsWithId(updatedContacts); // Updating the contacts with Ids state
    formik.setFieldValue(
      'contacts',
      updatedContacts.map(({ id, ...rest }) => rest),
    ); // Updating the main Formik state and removing ids
  };

  // EDIT
  const handleEditContact = (id: any) => {
    const contactToEdit = contactsWithId?.find((contact) => contact.id === id);
    if (contactToEdit) {
      const { id, ...contactWithoutId } = contactToEdit;
      formikContact.setValues(contactWithoutId);
      setContactBeforeEdit(contactToEdit);
      handleDeleteContact(id); // Remove the contact temporarily from the list
      setEditMode(true);
    } else {
      console.log('Contact not found with ID:', id);
    }
  };

  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'contactName',
        headerName: 'Name',
        width: 110,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Name"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'address',
        headerName: 'Address',
        width: 110,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Address"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'city',
        headerName: 'City',
        width: 90,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="City"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'state',
        headerName: 'State',
        width: 60,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="State"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'zipCode',
        headerName: 'Zip Code',
        width: 65,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Zip Code"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'country',
        headerName: 'Country',
        width: 65,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Country"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'position',
        headerName: 'Position',
        width: 70,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Position"
            keyPrefix={KEY_ADD_CUSTOMER}
            params={params}
          />
        ),
      },
      {
        field: 'phone',
        headerName: 'Tel. No.',
        width: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Tel. No."
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Email"
            keyPrefix={KEY_GLOBAL_COMMON}
            params={params}
          />
        ),
      },
      {
        field: 'preferredCommunication',
        headerName: 'Preferred Contact',
        width: 130,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Preferred Contact"
            keyPrefix={KEY_ADD_CUSTOMER}
            params={params}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 80,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Actions"
            keyPrefix={KEY_GLOBAL_BUTTONS}
            params={params}
          />
        ),
        renderCell: (params) => {
          const handleEdit = () => {
            handleEditContact(params.id);
          };

          const handleDelete = () => {
            handleDeleteContact(params.id);
          };

          return (
            <div>
              <IconButton disabled={viewMode} onClick={handleEdit}>
                <EditIcon />
              </IconButton>
              <IconButton disabled={viewMode} onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </div>
          );
        },
      },
    ],
    [viewMode, contactsWithId, handleEditContact, handleDeleteContact],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  const handleEditContactSave = () => {
    const newContact = { ...formikContact.values, id: genUUID() };
    const updatedContacts = [...(contactsWithId || []), newContact]; // Adding back edited contact

    setContactsWithId(updatedContacts); // Updating the contacts with Ids state
    formik.setFieldValue(
      'contacts',
      updatedContacts.map(({ id, ...rest }) => rest),
    ); // Updating the main Formik state and removing ids

    formikContact.resetForm(); // Reseting the secondary Formik form
    setEditMode(false);
  };

  // Cancel
  const handleEditContactCancel = () => {
    const oldContact = { ...contactBeforeEdit, id: genUUID() };
    const updatedContacts = [...(contactsWithId || []), oldContact]; // Restoring back the original contact data

    setContactsWithId(updatedContacts); // Updating the contacts with Ids state
    formik.setFieldValue(
      'contacts',
      updatedContacts.map(({ id, ...rest }) => rest),
    ); // Updating the main Formik state and removing ids

    formikContact.resetForm(); // Reset the secondary Formik form
    setContactBeforeEdit(undefined); // Reset the saved original contact data
    setEditMode(false);
  };

  // CREATE

  const handleAddContact = () => {
    formikContact.validateForm().then((errors) => {
      if (Object.keys(errors).length === 0 && formikContact.isValid) {
        // Ensures there are no validation errors
        const newContact: ContactWithId = {
          ...formikContact.values,
          id: genUUID(),
        };
        const updatedContacts = [...(contactsWithId || []), newContact];

        setContactsWithId(updatedContacts); // Updating the contacts with Ids state
        formik.setFieldValue(
          'contacts',
          updatedContacts.map(({ id, ...rest }) => rest),
        ); // Updating the main Formik state and removing ids

        formikContact.resetForm(); // Reset the secondary Formik form
      } else {
        // Making sure to touch all fields to show errors
        formikContact.setTouched({
          contactName: true,
          position: true,
          address: true,
          email: true,
          city: true,
          state: true,
          zipCode: true,
          phone: true,
          preferredCommunication: true,
          country: true,
        });
      }
    });
  };

  useEffect(() => {
    // Because when tab switches, the state of this tab dies and when user comes back we need to put it in again
    const fetchData = async () => {
      const updatedContactsWithId = formik.values.contacts?.map((contact) => ({
        ...contact,
        id: genUUID(),
      }));

      if (updatedContactsWithId) {
        setContactsWithId(updatedContactsWithId);
      }
    };
    fetchData();
  }, []);

  return (
    <>
      <Typography color="primary">
        {tAddC('ADD ADDITIONAL CONTACTS')}
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <div className="grid w-full grid-flow-col grid-rows-6 gap-4 webkit-box">
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tAddC('Contact Name')}
              name="contactName"
              disabled={viewMode}
              error={Boolean(
                getFormikField(formikContact, 'contactName').error,
              )}
              helperText={getFormikField(formikContact, 'contactName').error}
              value={getFormikField(formikContact, 'contactName').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('Address')}
              name="address"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'address').error)}
              helperText={getFormikField(formikContact, 'address').error}
              value={getFormikField(formikContact, 'address').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('City')}
              name="city"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'city').error)}
              helperText={getFormikField(formikContact, 'city').error}
              value={getFormikField(formikContact, 'city').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('Zip')}
              name="zipCode"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'zipCode').error)}
              helperText={getFormikField(formikContact, 'zipCode').error}
              value={getFormikField(formikContact, 'zipCode').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tGC('State')}
              select
              name="state"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'state').error)}
              helperText={getFormikField(formikContact, 'state').error}
              value={getFormikField(formikContact, 'state').value}
              onChange={formikContact.handleChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {usaStates.map((state) => (
                <MenuItem key={state.abbreviation} value={state.abbreviation}>
                  {state.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <div className="flex justify-center">
            <TextField
              disabled
              variant="filled"
              {...textFieldProps}
              {...textFieldSelectProps}
              label={tGC('Country')}
              select
              name="country"
              error={Boolean(getFormikField(formikContact, 'country').error)}
              helperText={getFormikField(formikContact, 'country').error}
              value={getFormikField(formikContact, 'country').value}
              onChange={formikContact.handleChange}
            >
              <MenuItem value="US">US</MenuItem>
            </TextField>
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tAddC('Position')}
              name="position"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'position').error)}
              helperText={getFormikField(formikContact, 'position').error}
              value={getFormikField(formikContact, 'position').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('Tel. No.')}
              name="phone"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'phone').error)}
              helperText={getFormikField(formikContact, 'phone').error}
              value={getFormikField(formikContact, 'phone').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('Email')}
              name="email"
              disabled={viewMode}
              error={Boolean(getFormikField(formikContact, 'email').error)}
              helperText={getFormikField(formikContact, 'email').error}
              value={getFormikField(formikContact, 'email').value}
              onChange={formikContact.handleChange}
            />
          </div>
          <div className="flex justify-center">
            <TextField
              variant="filled"
              {...textFieldProps}
              label={tGC('Preferred Method of Communication')}
              name="preferredCommunication"
              disabled={viewMode}
              error={Boolean(
                getFormikField(formikContact, 'preferredCommunication').error,
              )}
              helperText={
                getFormikField(formikContact, 'preferredCommunication').error
              }
              value={
                getFormikField(formikContact, 'preferredCommunication').value
              }
              onChange={formikContact.handleChange}
            />
          </div>
        </div>
        <Box sx={{ paddingX: 5 }}>
          <Box
            sx={{
              paddingY: 1,
              paddingX: 1,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            {editMode ? (
              <>
                <Tooltip title={tGlobal('Save')}>
                  <IconButton
                    disabled={viewMode}
                    size="small"
                    sx={{ p: 0, marginRight: 2 }}
                    color="primary"
                    onClick={handleEditContactSave}
                  >
                    <SaveIcon cursor={'pointer'} fontSize={'large'} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tGlobal('Cancel')}>
                  <IconButton
                    disabled={viewMode}
                    size="small"
                    sx={{ p: 0 }}
                    onClick={handleEditContactCancel}
                  >
                    <CancelIcon cursor={'pointer'} fontSize={'large'} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Tooltip title={tGlobal('Add Contact')}>
                <IconButton disabled={viewMode}>
                  <AddCircleIcon
                    onClick={handleAddContact}
                    cursor={'pointer'}
                    fontSize={'large'}
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>
      <Typography color="primary" sx={{ marginTop: 2 }}>
        {tAddC('CONTACTS')}
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <div style={{ height: 200, width: '100%' }}>
        <DataGridPro
          apiRef={apiRef}
          columnHeaderHeight={56}
          sx={{
            border: 0, // Removes all borders
            '& .MuiDataGrid-cell': {
              padding: columnPadding, // Adjust the padding as needed
            },
            '& .MuiDataGrid-columnHeader': {
              padding: columnPadding, // Adjust the padding as needed
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
          hideFooter
          disableColumnSorting
          disableColumnFilter
          columnVisibilityModel={persistedPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={
            persistedPrefs.onColumnVisibilityModelChange
          }
          onColumnWidthChange={persistedPrefs.onColumnWidthChange}
          onColumnOrderChange={persistedPrefs.onColumnOrderChange}
          rows={contactsWithId}
          columns={columns}
          pageSizeOptions={[contactsWithId?.length || 0]} // Make sure this number is equal to or greater than the number of rows to display all at once
        />
      </div>
      <Divider sx={{ marginY: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
          disabled={viewMode}
          variant="filled"
          label={tAddC('Notes')}
          multiline
          rows={4}
          fullWidth
          sx={{
            backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
          }}
          name="notes"
          error={Boolean(getFormikField(formik, 'notes').error)}
          helperText={getFormikField(formik, 'notes').error}
          value={getFormikField(formik, 'notes').value}
          onChange={formik.handleChange}
        />
      </Box>
    </>
  );
}
